/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  OperatorProp,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import {
  baseSearchFieldProps,
  updateSearchFieldDataType,
} from '../SearchTextField/SearchTextFieldManager';
import { defaultValue } from './SearchLookupField';

const SearchLookupFieldManager: ComponentManagerFn<'SearchLookupField'> = (
  platform: Platform,
  type: 'SearchLookupField',
): ComponentManager<'SearchLookupField'> => {
  return {
    ...baseComponentManager<'SearchLookupField'>(platform, 'SearchLookupField'),
    type: 'SearchLookupField',
    defaultValue,
    props: {
      ...baseSearchFieldProps,
      viewAttribute: {
        ...(baseSearchFieldProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: () => [
          'Decimal',
          'Double',
          'Email',
          'Integer',
          'Reference',
          'String',
          'UserID',
          'YN',
          'TF',
        ],
      },
      lookup: {
        label: 'Lookup',
        type: 'LookupCodeChooser',
        required: true,
      },
      defaultOperator: {
        ...(baseSearchFieldProps.defaultOperator as OperatorProp),
        getDataType: async () => {
          return 'Select';
        },
      },
    },
    isPropertyVisible(prop, itemData, context): boolean {
      switch (prop) {
        case 'dataType':
        case 'updatable':
        case 'insertable': {
          return false;
        }
        default:
          return true;
      }
    },
    onPropertyChange: (property, value, itemProps, context) => {
      if (property === 'viewAttribute') {
        updateSearchFieldDataType(type, value, context);
      }
      return { [property]: value };
    },
    getDisplayText: (item) =>
      typeof item.props.label === 'string'
        ? item.props.label
        : item.props.label.value ?? '',
    replaceableWith: [
      'SearchCheckbox',
      'SearchLookupField',
      'SearchNumberField',
      'SearchSelectField',
      'SearchTextField',
    ],
    name: 'Search Lookup Field',
    category: 'Search',
    icon: { iconName: 'io-select', iconType: 'cloudio' },
    description: `**Overview**<br/>
The Search Lookup Field is a specialized input component for **Search Forms**. It allows users to filter records by selecting values from a pre-defined "Lookup" (a standardized set of reference data or system codes). Unlike a simple text search, this ensures users strictly search for valid, existing values within the system.
Use this component when filtering by standardized categories, foreign keys, or status codes defined in your application's Lookup system.

**1. Configuration**<br/>
* **Lookup (Required):** The core property of this component. You must select a specific "Lookup" definition from your application. This defines the list of options (e.g., "Countries," "Order Statuses," or "Product Categories") that will be presented to the user in a searchable dropdown or modal picker.
* **Default Operator:** The component automatically configures the search operator (typically **"Equals"** or **"In"**) to match the behavior of a selection-based filter, ensuring the query logic is correct for reference data.

**2. Data Compatibility**<br/>
* **View Attribute:** This component is highly versatile and can bind to many data types, provided they correspond to the keys defined in your chosen Lookup:
    * **Text:** String or Email fields.
    * **Numbers:** Integer, Decimal, or Double (typically used for ID-based lookups).
    * **References:** Reference or UserID fields.
    * **Flags:** YN (Yes/No) or TF (True/False) logic.

**3. Behavior**<br/>
* **Query-Only Logic:** As a search component, properties like **Updatable** and **Insertable** are hidden. Its sole purpose is query generation, not the modification of data records.
* **Interchangeability:** If your requirements change (e.g., you decide to allow free-text entry instead of a strict lookup), this component can be swapped with a **Search Text Field** or **Search Select Field** without needing to delete and re-configure the element.

**4. Best Use Cases**<br/>
* **Foreign Keys:** Searching for orders belonging to a specific "Customer" where the customer list is a Reference Lookup.
* **Standardized Codes:** Filtering a list of tickets by "Priority" (High, Medium, Low) or "Department" codes defined in a global Lookup.
* **Geographic Filters:** Allowing users to pick a "State" or "Country" from a standardized list to filter global shipping addresses.
* **Asset Management:** Finding equipment assigned to a specific "Location Code" or "Maintenance Category."`,
  };
};

export default SearchLookupFieldManager;
